import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Spin,
  Typography,
  Tag,
  Modal,
  notification,
  Divider,
  Tooltip,
  Result,
} from "antd";
import {
  CheckCircleFilled,
  RobotOutlined,
  ThunderboltFilled,
  SafetyCertificateFilled,
  CreditCardOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Home2, Crown1, Flash, DirectRight } from "iconsax-react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/layouts/Layout";
import SettingLayout from "../Setting/Components/SettingLayout";
import BreadcrumbBlock from "../../components/BreadcrumbBlock/BreadcrumbBlock";
import { mixPostV1 } from "../../services/mixin";
import SelectPaymentMethodModal from "../../components/Modal/SelectPaymentMethodModal";
import PaymentModal from "../../components/Modal/PaymentModal";
import "./style.scss";


interface PackageItem {
  key: string;
  type_id: number;
  service_id: number;
  type_name: string;
  income: number | null;
  duration: number | null;
}

const { Title, Text, Paragraph } = Typography;

const serviceNames: Record<number, string> = {
  1: "Full Service",
  2: "Match Only",
  3: "Full Service + Automation Report",
};

const getFeaturesForService = (serviceId: number): string[] => {
  switch (serviceId) {
    case 3:
      return [
        "Automated revenue & shift report notifications sent via Telegram",
        "Real-time instant status updates for Store Owners & Managers",
        "Configure Bots & Chat IDs for automated reporting",
        "Full store reports, game & cash-out management",
        "24/7 Priority VIP support",
      ];
    case 1:
      return [
        "Full store reports & game management",
        "Real-time revenue & machine income tracking",
        "Staff match & cash-out permission controls",
        "Standard support & store updates",
      ];
    case 2:
      return [
        "Match only management",
        "Basic member & game activity log",
        "Standard store configuration",
      ];
    default:
      return [
        "Store management tools",
        "Standard features access",
      ];
  }
};

const UpgradePackage: React.FC = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPackage, setSelectedPackage] = useState<PackageItem | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Store Plan & Payment states from setting/payment-method
  const [planInfo, setPlanInfo] = useState<any>(null);
  const [comboCards, setComboCards] = useState<any[]>([]);
  const [publishKey, setPublishKey] = useState<string>("");
  const [showSelectPaymentMethod, setShowSelectPaymentMethod] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);

  const storeTypeId = Number(localStorage.getItem("storeTypeId") || "0");
  const currentServiceId = Number(localStorage.getItem("service_id") || "0");
  const storeId = localStorage.getItem("storeId") || "";

  const activePackage = packages.find((p) => p.type_id === storeTypeId);
  const activePackageDisplayName = planInfo?.plan_name || activePackage?.type_name || serviceNames[currentServiceId] || "Full Service";
  const currentPrice = Number(activePackage?.income ?? planInfo?.income ?? 0);

  const [api, contextHolder] = notification.useNotification();

  // Fetch Packages List
  const getPackages = async () => {
    try {
      setLoading(true);
      const res = await mixPostV1("/api/webAdmin/gSettingTypeStore.php", {});
      if (res.status && Array.isArray(res.data)) {
        setPackages(
          res.data.map((item: any, idx: number) => ({
            key: item.type_id ?? idx,
            type_id: Number(item.type_id),
            type_name: item.type_name ?? `Package ${idx + 1}`,
            service_id: Number(item.service_id ?? 1),
            duration: item.duration ? Number(item.duration) : null,
            income: item.income ? Number(item.income) : null,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Current Plan & Expiration Date from gListPaymentCard.php
  const getStorePlanInfo = async () => {
    try {
      const res = await mixPostV1("/api/webAdmin/gListPaymentCard.php", {});
      if (res.status === 1 && res.data) {
        setPlanInfo(res.data.infoPlanStore || null);
      }
    } catch (error) {
      console.error("Error fetching plan info:", error);
    }
  };

  // Fetch Stripe Publish Key
  const getStripeKey = async () => {
    try {
      const res = await mixPostV1("/api/webAdmin/gKeyStripe.php", {});
      if (res.status === 1 && res.data?.public_key) {
        setPublishKey(res.data.public_key);
      }
    } catch (error) {
      console.error("Error fetching stripe key:", error);
    }
  };

  // Fetch Store Saved Payment Cards
  const getComboCards = async () => {
    try {
      const res = await mixPostV1("/api/webAdmin/gComboCardByStore.php", {});
      if (res.status === 1 && Array.isArray(res.data)) {
        const cards = res.data.map((val: any) => ({
          key: val.card_id.toString(),
          label: `${val.brand} ****${val.last_number} ${val.card_name}`.trim(),
          icon: (
            <img
              src={
                val.brand === "VISA"
                  ? "/images/banks/visa.svg"
                  : "/images/banks/mastercard.svg"
              }
              alt=""
              style={{ width: 32 }}
            />
          ),
        }));
        setComboCards(cards);
      }
    } catch (error) {
      console.error("Error fetching combo cards:", error);
    }
  };

  useEffect(() => {
    getPackages();
    getStorePlanInfo();
    getStripeKey();
    getComboCards();
  }, []);

  // Calculate Remaining Days until Expiration
  const getRemainingDays = (): number => {
    if (!planInfo?.date_expired) return 0;
    const rawDateStr = String(planInfo.date_expired).trim();
    const formattedDateStr = rawDateStr.includes(" ") ? rawDateStr.replace(" ", "T") : rawDateStr;
    const expireDate = new Date(formattedDateStr.includes("T") ? formattedDateStr : `${formattedDateStr}T00:00:00`);

    if (isNaN(expireDate.getTime())) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const targetDate = new Date(expireDate);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  };

  // Prorated Upgrade Calculation Formula:
  // 1. Unused Value of current active plan = currentPrice * (remainingDays / (currentDuration * 30))
  // 2. Upgrade Amount Due Today = newPrice - unusedValue
  const calculateUpgradeDetails = (newPrice: number | null, _duration?: number | null) => {
    const currentDuration = Number(planInfo?.duration ?? activePackage?.duration ?? 1);
    const currentTotalDays = currentDuration > 0 ? currentDuration * 30 : 30;

    if (!newPrice) {
      return { upgradeAmount: 0, priceDiff: 0, remainingDays: 0, totalDays: currentTotalDays, unusedValue: 0 };
    }

    const remainingDays = getRemainingDays();
    const priceDiff = newPrice - currentPrice;

    if (priceDiff > 0 && remainingDays > 0 && currentPrice > 0) {
      const unusedValue = (currentPrice * remainingDays) / currentTotalDays;
      const calculatedAmount = newPrice - unusedValue;
      const roundedUnused = Math.round(unusedValue * 100) / 100;
      const roundedUpgrade = Math.max(0, Math.round(calculatedAmount * 100) / 100);

      return {
        upgradeAmount: roundedUpgrade,
        priceDiff,
        remainingDays,
        totalDays: currentTotalDays,
        unusedValue: roundedUnused,
      };
    }

    return {
      upgradeAmount: newPrice,
      priceDiff,
      remainingDays,
      totalDays: currentTotalDays,
      unusedValue: 0,
    };
  };

  const handleSelectPackage = (pkg: PackageItem) => {
    setSelectedPackage(pkg);
    setConfirmModalOpen(true);
  };

  const handleProceedToPayment = async () => {
    if (!selectedPackage) return;
    const pkgIncome = Number(selectedPackage.income ?? 0);
    const isHigherTier = pkgIncome > currentPrice;
    const isExpired = planInfo?.is_expired === 1;

    // If switching to lower/equal tier package while store is active (No payment required by backend)
    if (!isHigherTier && !isExpired) {
      setSubmitting(true);
      try {
        const res = await mixPostV1("/api/webAdmin/upgradeHightLevelPackedStore.php", {
          type_id: selectedPackage.type_id,
        });

        if (res.status === 1) {
          api.success({
            message: "Package Changed Successfully!",
            description:
              res.message ||
              `Your store package has been updated to "${selectedPackage.type_name}".`,
            placement: "bottomRight",
          });
          localStorage.setItem("storeTypeId", String(selectedPackage.type_id));
          localStorage.setItem("service_id", String(selectedPackage.service_id));
          getPackages();
          getStorePlanInfo();
        } else {
          api.error({
            message: "Package Change Failed",
            description: res.message || "Failed to update package.",
            placement: "bottomRight",
          });
        }
      } catch (error) {
        api.error({
          message: "Package Change Failed",
          description: "An error occurred while updating package.",
          placement: "bottomRight",
        });
      } finally {
        setSubmitting(false);
        setConfirmModalOpen(false);
      }
      return;
    }

    setConfirmModalOpen(false);
    await getComboCards();
    setShowSelectPaymentMethod(true);
  };


  return (
    <>
      {contextHolder}

      {/* Stripe Select Payment Method Modal */}
      <SelectPaymentMethodModal
        isShow={showSelectPaymentMethod}
        publishKey={publishKey}
        amount={
          selectedPackage
            ? calculateUpgradeDetails(selectedPackage.income, selectedPackage.duration).upgradeAmount
            : 0
        }
        planName={
          selectedPackage
            ? `Upgrade to ${selectedPackage.type_name}`
            : "Plan Upgrade"
        }
        typeId={selectedPackage?.type_id}
        apiEndpoint="/api/webAdmin/upgradeHightLevelPackedStore.php"
        savedCardApiEndpoint="/api/webAdmin/upgradePackedByCardOld.php"
        paymentMethods={[
          ...comboCards,
          {
            key: "card",
            label: "Card",
            icon: (
              <img
                src="/images/banks/mastercard.svg"
                alt=""
                style={{ width: 32 }}
              />
            ),
          },
        ]}
        onCancel={() => setShowSelectPaymentMethod(false)}
        onSelectCard={() => {
          setShowSelectPaymentMethod(false);
          setShowPaymentModal(true);
        }}
        onPaymentSuccess={() => {
          api.success({
            message: "Package Upgrade Successful!",
            description: `Your store plan has been upgraded to ${selectedPackage?.type_name}.`,
            placement: "bottomRight",
          });
          if (selectedPackage) {
            localStorage.setItem("storeTypeId", String(selectedPackage.type_id));
            localStorage.setItem("service_id", String(selectedPackage.service_id));
          }
          getPackages();
          getStorePlanInfo();
        }}
      />

      {/* Manual Payment Modal */}
      <PaymentModal
        isShow={showPaymentModal}
        storeId={storeId}
        onModalClose={() => setShowPaymentModal(false)}
        onModalSubmit={() => {
          setShowPaymentModal(false);
          api.success({
            message: "Payment Request Submitted!",
            description: "Your upgrade payment request has been received.",
            placement: "bottomRight",
          });
          getPackages();
          getStorePlanInfo();
        }}
      />

      <SettingLayout current="upgrade-package" contentClassName="upgrade-layout">
        <div className="upgrade-package-container">
          {/* Hero Header */}
          <div className="upgrade-hero">
            <div className="hero-badge">
              <Crown1 size={18} variant="Bulk" />
              <span>STORE SERVICE UPGRADE</span>
            </div>
            <h1 className="hero-title">Select the Best Plan for Your Store</h1>
            <p className="hero-subtitle">
              Scale your store operations effortlessly with automated reports, instant Telegram bot alerts, and comprehensive match tracking.
            </p>
            <div className="current-plan-banner">
              <SafetyCertificateFilled className="plan-icon" />
              <span className="banner-label">Current Store Plan: </span>
              <strong className="banner-plan-name">{activePackageDisplayName}</strong>
              {planInfo?.date_expired && (
                <div
                  className={`expire-badge ${
                    planInfo?.is_expired === 1 ? "is-expired" : "is-active"
                  }`}
                >
                  <CalendarOutlined style={{ fontSize: 13 }} />
                  <span>
                    {planInfo?.is_expired === 1
                      ? `Expired on ${planInfo.date_expired}`
                      : `Expires on ${planInfo.date_expired} (${getRemainingDays()} days left)`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Package Cards Section */}
          {loading ? (
            <Flex justify="center" align="center" style={{ minHeight: 300 }}>
              <Spin size="large" tip="Loading available packages..." />
            </Flex>
          ) : (
            <div className="packages-grid">
              {packages.map((pkg) => {
                const isCurrent =
                  pkg.type_id === storeTypeId ||
                  (storeTypeId === 0 && pkg.service_id === currentServiceId);
                const isRecommended = pkg.service_id === 3;
                const pkgIncome = Number(pkg.income ?? 0);
                const isHigherTier = pkgIncome > currentPrice;
                const features = getFeaturesForService(pkg.service_id);

                // Prorated upgrade price calculation
                const upgradeDetails = calculateUpgradeDetails(pkg.income, pkg.duration);

                return (
                  <div
                    key={pkg.key}
                    className={`package-card ${isCurrent ? "active-plan" : isRecommended ? "recommended" : ""}`}
                  >
                    {isCurrent ? (
                      <div className="current-plan-tag">ACTIVE PLAN</div>
                    ) : isRecommended ? (
                      <div className="popular-tag">MOST POPULAR</div>
                    ) : null}

                    <div>
                      <div className="card-header">
                        <div className="pkg-name">{pkg.type_name}</div>
                        <span className={`service-badge service-${pkg.service_id}`}>
                          {serviceNames[pkg.service_id] || "Standard Service"}
                        </span>
                      </div>

                      <div className="card-price">
                        <div className="amount">
                          <span className="currency">$</span>
                          {pkg.income !== null ? pkg.income.toLocaleString() : "0"}
                        </div>
                        <div className="duration">
                          {pkg.duration ? `Duration: ${pkg.duration} Month(s)` : "Monthly"}
                        </div>
                        {!isCurrent && isHigherTier && upgradeDetails.upgradeAmount < pkgIncome && (
                          <div style={{ marginTop: 6, fontSize: 13, color: "#10b981", fontWeight: 600 }}>
                            Prorated Upgrade: ${upgradeDetails.upgradeAmount} (${upgradeDetails.unusedValue} credit applied)
                          </div>
                        )}
                      </div>

                      <div className="card-features">
                        <div className="features-title">Includes Features:</div>
                        {features.map((feat, i) => (
                          <div key={i} className="feature-item">
                            <CheckCircleFilled className="check-icon" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="card-action">
                      {isCurrent ? (
                        <Button className="upgrade-btn btn-active" disabled>
                          Current Active Plan
                        </Button>
                      ) : (
                        <Button
                          type={isRecommended ? "primary" : "default"}
                          className={`upgrade-btn ${isRecommended ? "btn-recommended" : "btn-standard"}`}
                          onClick={() => handleSelectPackage(pkg)}
                        >
                          {isHigherTier ? "Upgrade Now" : "Switch Plan"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upgrade & Prorated Calculation Confirmation Modal */}
        <Modal
          open={confirmModalOpen}
          onCancel={() => setConfirmModalOpen(false)}
          footer={null}
          centered
          width={480}
          destroyOnClose
          styles={{
            content: {
              borderRadius: 20,
              padding: 30,
              background: "#1e293b",
              color: "#fff",
              border: "1px solid rgba(255, 255, 255, 0.15)",
            },
          }}
        >
          {selectedPackage && (() => {
            const details = calculateUpgradeDetails(selectedPackage.income, selectedPackage.duration);
            const isHigherTier = Number(selectedPackage.income ?? 0) > currentPrice;
            const isExpired = planInfo?.is_expired === 1;
            const isFreeSwitch = !isHigherTier && !isExpired;

            return (
              <div>
                <Flex vertical align="center" gap={12} style={{ textAlign: "center", marginBottom: 20 }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "linear-gradient(145deg, #fbe18d 0%, #f4c93b 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#111",
                      fontSize: 32,
                      boxShadow: "0 8px 24px rgba(244, 201, 59, 0.4)",
                    }}
                  >
                    <CreditCardOutlined />
                  </div>
                  <Title level={4} style={{ margin: 0, fontWeight: 700, color: "#ffffff" }}>
                    {isFreeSwitch ? "Confirm Package Switch" : "Upgrade Plan Breakdown"}
                  </Title>
                  <Text style={{ color: "#94a3b8" }}>
                    {isFreeSwitch
                      ? "Switching to this plan is free for your remaining subscription period."
                      : "Review your prorated upgrade calculation before card payment."}
                  </Text>
                </Flex>

                <div
                  style={{
                    background: "rgba(15, 23, 42, 0.8)",
                    borderRadius: 14,
                    padding: 20,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    marginBottom: 24,
                  }}
                >
                  <Flex justify="space-between" style={{ marginBottom: 10 }}>
                    <Text style={{ color: "#94a3b8" }}>Current Plan:</Text>
                    <Text strong style={{ color: "#ffffff" }}>
                      {activePackageDisplayName} (${currentPrice})
                    </Text>
                  </Flex>
                  <Flex justify="space-between" style={{ marginBottom: 10 }}>
                    <Text style={{ color: "#94a3b8" }}>New Plan:</Text>
                    <Text strong style={{ color: "#f4c93b" }}>
                      {selectedPackage.type_name} (${selectedPackage.income})
                    </Text>
                  </Flex>
                  <Flex justify="space-between" style={{ marginBottom: 10 }}>
                    <Text style={{ color: "#94a3b8" }}>Current Plan Credit (26 days left):</Text>
                    <Text strong style={{ color: "#34d399" }}>
                      -${details.unusedValue}
                    </Text>
                  </Flex>

                  {!isFreeSwitch && details.priceDiff > 0 && details.remainingDays > 0 && currentPrice > 0 && (
                    <div
                      style={{
                        background: "rgba(244, 201, 59, 0.1)",
                        border: "1px dashed rgba(244, 201, 59, 0.4)",
                        borderRadius: 10,
                        padding: "10px 14px",
                        margin: "12px 0",
                        fontSize: 13,
                        color: "#cbd5e1",
                      }}
                    >
                      <Flex align="center" gap={6} style={{ marginBottom: 4 }}>
                        <InfoCircleOutlined style={{ color: "#f4c93b" }} />
                        <span style={{ fontWeight: 600, color: "#f4c93b" }}>Prorated Formula:</span>
                      </Flex>
                      <div>
                        ${selectedPackage.income} (New Plan) - ${details.unusedValue} (Unused Credit) = <b>${details.upgradeAmount}</b>
                      </div>
                    </div>
                  )}

                  <Divider style={{ borderColor: "rgba(255, 255, 255, 0.1)", margin: "14px 0" }} />
                  <Flex justify="space-between" align="center">
                    <Text strong style={{ fontSize: 16, color: "#ffffff" }}>
                      {isFreeSwitch ? "Package Switch Fee:" : "Prorated Fee Due Today:"}
                    </Text>
                    <Text strong style={{ fontSize: 24, color: "#34d399" }}>
                      {isFreeSwitch ? "$0 (Free)" : `$${details.upgradeAmount}`}
                    </Text>
                  </Flex>
                </div>

                <Flex gap={12}>
                  <Button
                    size="large"
                    onClick={() => setConfirmModalOpen(false)}
                    disabled={submitting}
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      background: "rgba(255, 255, 255, 0.08)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      color: "#fff",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleProceedToPayment}
                    loading={submitting}
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      background: "linear-gradient(145deg, #fbe18d, #f4c93b)",
                      color: "#111",
                      border: "none",
                      fontWeight: 700,
                      boxShadow: "0 4px 14px rgba(244, 201, 59, 0.35)",
                    }}
                  >
                    {isFreeSwitch ? "Confirm Switch Plan" : "Proceed to Card Payment"}
                  </Button>
                </Flex>
              </div>
            );
          })()}
        </Modal>
      </SettingLayout>
    </>
  );
};

export default UpgradePackage;
